import { useEffect, useRef } from 'react'

import {
  useLazyGetFeedFieldsQuery,
  useLazyGetFeedSchemaMappingSuggestionQuery,
} from '@Store/api/product'
import type { ProductColumnMatchInfo } from '@Store/types/product'
import { showErrorMessage, showToastMessage } from '@Utils/toast/toast-message'

import feedProps from '../../../constants/feedProps'
import useMatchFieldsReducer from '../../../hooks/useMatchFieldsReducer'
import MapFields from '../../match-field/MapFields'

/** The commit controls, handed to whatever hosts the mapping step. */
export type FeedMapFieldsActions = {
  save: () => void
  cancel: () => void
  saveLabel: string
  canSave: boolean
  saving: boolean
  /** Why saving is blocked, if it is — shown beside the buttons. */
  blockedReason: string | null
}

function FeedMapFields({
  visitFor,
  visitResultState,
  saveResultState,
  onSubmit,
  onCancel,
  onActionsChange,
}: {
  visitResultState: {
    isLoading: boolean
    isError: boolean
    isSuccess: boolean
    error: any
    data: any
    reset: () => void
  }
  saveResultState: {
    isUninitialized: boolean
    isLoading: boolean
    isError: boolean
    isSuccess: boolean
    error: any
  }
  visitFor: null | 'create' | 'edit'
  onSubmit: (
    mapping: { fieldId: number; name: string; value?: unknown }[],
  ) => void
  /** Closes the dialog the mapping step is rendered in. */
  onCancel?: () => void
  /**
   * Hands the commit controls to whatever is hosting this step.
   *
   * They used to be rendered at the end of the list and made sticky, which
   * put them inside the scrolling region — where the dialog's own padding
   * clipped them. The host puts them in its footer, outside the scroll.
   */
  onActionsChange?: (actions: FeedMapFieldsActions | null) => void
}): JSX.Element {
  const {
    isLoading: isVisitLoading,
    isError: isVisitError,
    isSuccess: isVisitSuccess,
    error: visitError,
    data: visitData,
    reset: resetVisitResult,
  } = visitResultState

  const { isLoading: isSaveLoading } = saveResultState

  const {
    state: {
      options,
      matchedProperties,
      unmatchedProperties,
      matchedSelected,
      unmatchedSelected,
      allSelected,
      requiredNotSelected = [],
    },
    actions: {
      setOptions,
      setMatchData,
      setMatchedSelected,
      removeMatchedSelected,
      setUnmatchedSelected,
      removeUnmatchedSelected,
      clearState: clearMatchSelected,
    },
  } = useMatchFieldsReducer()

  const [
    getFeedSchemaMappingSuggestion,
    {
      data: mappingSuggestionData,
      isLoading: isMappingSuggestionLoading,
      isSuccess: isMappingSuggestionSuccess,
    },
  ] = useLazyGetFeedSchemaMappingSuggestionQuery()

  const [
    getFeedFields,
    {
      data: feedFieldsData,
      isLoading: isFeedFieldsLoading,
      isSuccess: isFeedFieldsSuccess,
      isError: isFeedFieldsError,
    },
  ] = useLazyGetFeedFieldsQuery()

  // Whether the dialog still has nothing worth showing.
  //
  // "The feed was read" is too early to render by, in three separate ways.
  // getFeedSchemaMappingSuggestion is a lazy query, so it reports
  // isLoading === false until something triggers it — leaving a gap in which
  // the visit has succeeded, no suggestion is in flight yet, and the content
  // branch renders with no rows at all. That flash is the header and the note
  // with an empty space between them, and it is followed by the skeleton
  // coming back, so the dialog appears to load twice.
  //
  // matchedProperties/unmatchedProperties only fill once the field list and
  // the suggestion have both returned and setMatchData has run, so they are
  // the first honest signal that there is something to look at. Guarded on the
  // feed actually having columns, or a feed with none would spin forever
  // instead of reaching the "no properties found" path.
  const feedColumnCount = visitData?.data?.length ?? 0
  const hasMappingRows =
    (matchedProperties?.length ?? 0) + (unmatchedProperties?.length ?? 0) > 0
  const isPreparing =
    isVisitLoading ||
    isFeedFieldsLoading ||
    isMappingSuggestionLoading ||
    (isVisitSuccess && feedColumnCount > 0 && !hasMappingRows)

  const handleSaveMapping = (): void => {
    const selected = allSelected ?? []

    // When /conveyor/field fails we fall back to static feedProps, which
    // carry no numeric field IDs. Submitting `{ fieldId: undefined }` would be
    // rejected by the backend, so surface an error instead of a bad payload.
    if (selected.some(({ id }) => typeof id !== 'number')) {
      showErrorMessage({
        message:
          'Field mapping could not be loaded correctly. Please refresh and try again.',
      })
      return
    }

    onSubmit(
      // `id` is guaranteed numeric by the guard above; `name` is always
      // present on a selected column.
      selected.map(({ name, id, initialValue }) => ({
        fieldId: id as number,
        name: name as string,
        value: initialValue,
      })),
    )
  }

  // The host stores what it is given, which re-renders this component, which
  // rebuilds `allSelected` and `requiredNotSelected` as fresh arrays — so
  // depending on those arrays here published again on every render and ran the
  // update depth out. What the footer actually shows is four primitives, and
  // only a change in one of those is worth republishing for.
  const isMappingReady = isVisitSuccess && !!visitData?.data && !isPreparing
  const saveLabel =
    visitFor === 'create' ? 'Save Feed Integration' : 'Save Mapping'
  const canSave = requiredNotSelected.length === 0 && !isSaveLoading
  const blockedReason = requiredNotSelected.length
    ? `${requiredNotSelected
        .map((field) => field.text)
        .join(', ')} must be mapped before your data can be imported.`
    : null

  // The callbacks close over the current selection, so they are read through a
  // ref at call time rather than being part of what triggers a republish.
  const latest = useRef({ save: handleSaveMapping, cancel: () => {} })

  useEffect(() => {
    if (!onActionsChange) return

    onActionsChange(
      isMappingReady
        ? {
            save: () => latest.current.save(),
            cancel: () => latest.current.cancel(),
            saveLabel,
            canSave,
            saving: isSaveLoading,
            blockedReason,
          }
        : null,
    )
  }, [
    onActionsChange,
    isMappingReady,
    saveLabel,
    canSave,
    isSaveLoading,
    blockedReason,
  ])

  const handleCancelMapping = (): void => {
    clearMatchSelected()
    resetVisitResult()
    onCancel?.()
  }

  latest.current = { save: handleSaveMapping, cancel: handleCancelMapping }

  useEffect(() => {
    if (!isVisitSuccess) {
      return
    }
    showToastMessage({ message: 'Feed target visited successfully.' })

    const seenTargetFields = (visitData?.data || []).map(
      (item: ProductColumnMatchInfo) => item.name,
    )

    if (!seenTargetFields.length) {
      showErrorMessage({
        message: 'No properties found in the feed.',
      })
      // TODO: send event to mixpanel/intercom about this
      return
    }

    getFeedSchemaMappingSuggestion({
      fields: seenTargetFields,
      // The preview already read a record, so send the values with the names:
      // a 13-digit number is a barcode and an http value is a URL whatever the
      // column happens to be called.
      samples: (visitData?.data || []).reduce(
        (acc: Record<string, string>, item: ProductColumnMatchInfo) => {
          if (
            item?.value === null ||
            item?.value === undefined ||
            Array.isArray(item.value) ||
            typeof item.value === 'object'
          ) {
            return acc
          }

          return { ...acc, [item.name]: String(item.value) }
        },
        {} as Record<string, string>,
      ),
    })

    getFeedFields(undefined)
  }, [isVisitSuccess, visitData])

  useEffect(() => {
    if (isVisitError) {
      showErrorMessage({
        message: 'Failed to visit feed target.',
      })
    }
  }, [isVisitError, visitError])

  useEffect(() => {
    if (isFeedFieldsSuccess) {
      setOptions(
        feedFieldsData?.map(({ name, label, mandatory, id }: any) => ({
          key: name,
          text: label,
          required: Number(mandatory) === 1,
          id,
        })) || feedProps,
      )
    } else if (isFeedFieldsError) {
      setOptions(feedProps)
    }
  }, [isFeedFieldsSuccess, isFeedFieldsError, feedFieldsData])

  useEffect(() => {
    if (!options?.length) {
      return
    }
    if (isMappingSuggestionSuccess && mappingSuggestionData) {
      setMatchData(
        (visitData?.data || []).reduce(
          (acc: ProductColumnMatchInfo[], item: ProductColumnMatchInfo) => {
            if (Array.isArray(item?.value) || typeof item?.value === 'object') {
              return acc
            }

            return [
              ...acc,
              {
                ...item,
                mappedName: mappingSuggestionData?.[item?.name] || null,
              },
            ]
          },
          [] as ProductColumnMatchInfo[],
        ),
        'mappedName',
        'key',
      )
    }
  }, [options, isMappingSuggestionSuccess, mappingSuggestionData, visitData])

  return (
    <div id="feed_map_fields">
      {isPreparing ? (
        <div className="border border-solid border-gray-200 rounded-lg p-4">
          {/* A bare skeleton leaves the user watching a blank box wondering
              whether anything is happening. Naming the step also sets the
              expectation that a large feed takes a moment. */}
          <p className="text-gray-700 font-medium text-base mb-1">
            {isVisitLoading
              ? 'Reading your feed…'
              : 'Matching your columns to Prisync properties…'}
          </p>
          <p className="text-gray-500 text-sm mb-4">
            {isVisitLoading
              ? 'Fetching the first record so we can show your columns and their values. Large feeds can take a moment.'
              : 'Suggesting a property for each column from its name and sample value. You can change any of them.'}
          </p>
          <div className="flex flex-col w-full">
            <div className="animate-pulse bg-gray-200 h-4 w-full rounded-md mt-2"></div>
            <div className="animate-pulse bg-gray-200 h-4 w-64 rounded-md mt-1.5"></div>
            <div className="animate-pulse bg-gray-200 h-4 w-24 rounded-md mt-2"></div>
          </div>
        </div>
      ) : isVisitError ? (
        <div className="border border-solid border-gray-200 rounded-lg p-4">
          <div className="flex flex-col w-full">
            <div className="text-red-500">Failed to visit feed target.</div>
            <div className="text-gray-500 text-sm mt-1">
              Please check the URL and authentication details and try again.
            </div>
          </div>
        </div>
      ) : isVisitSuccess && visitData?.data ? (
        <div className="flex min-w-0 flex-col">
          <div className="mb-4 rounded-lg border border-solid border-primary-200 bg-primary-25 p-3 text-sm text-gray-700">
            A Unique ID is required to match your products during sync. Map a
            stable identifier such as your SKU. You may also use another stable
            custom identifier that does not change across syncs.
          </div>
          <MapFields
            hideRequiredWarning
            state={{
              requiredNotSelected,
              options,
              allSelected,
              matchedProperties,
              matchedSelected,
              unmatchedProperties,
              unmatchedSelected,
            }}
            actions={{
              setMatchedSelected,
              removeMatchedSelected,
              setUnmatchedSelected,
              removeUnmatchedSelected,
            }}
          />
        </div>
      ) : (
        <></>
      )}
    </div>
  )
}

export default FeedMapFields
